import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Radio,
  SelectChangeEvent,
  CircularProgress,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import salesService from '../../../../services/salesService';
import { useTranslation } from 'react-i18next';

// Interfaces
export interface InventoryItemForSelection {
  id: string;
  name: string;
  barcode?: string;
  stockCode?: string;
  quantity?: number;
  unit?: string;
  salePrice?: number;
  imageUrl?: string | null;
  categoryId?: string | null;
}

interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId?: string | null;
  level?: number;
}

interface SingleProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  warehouses: Warehouse[];
  categories: InventoryCategory[];
  onSelectProduct: (product: InventoryItemForSelection) => void;
}

const SingleProductSelectionModal: React.FC<SingleProductSelectionModalProps> = ({
  open,
  onClose,
  companyId,
  warehouses,
  categories,
  onSelectProduct,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [items, setItems] = useState<InventoryItemForSelection[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Modal açıldığında state'leri sıfırla
  useEffect(() => {
    if (open) {
      setSelectedWarehouseId(null);
      setSelectedCategoryId(null);
      setSelectedProductId(null);
      setSearchTerm('');
      setPage(0);
    }
  }, [open]);

  // Ürünleri yükle
  useEffect(() => {
    if (!open || !companyId) return;

    const loadProducts = async () => {
      setLoading(true);
      try {
        console.log('📦 [SINGLE PRODUCT MODAL] Ürünler yükleniyor...', {
          page: page + 1,
          rowsPerPage,
          searchTerm,
          warehouseId: selectedWarehouseId,
          categoryId: selectedCategoryId,
        });

        const response = await salesService.getProductsPaged(
          page + 1,
          rowsPerPage,
          searchTerm || undefined,
          selectedWarehouseId || undefined,
          selectedCategoryId || undefined
        );

        const itemsArray = response.items || response.Items || [];
        const total = response.totalCount || response.TotalCount || 0;

        const parsedItems: InventoryItemForSelection[] = itemsArray.map((item: any) => ({
          id: item.id?.toString() || item.Id?.toString() || '',
          name: item.name || item.Name || '',
          barcode: item.barcode || item.Barcode || '',
          stockCode: item.stockCode || item.StockCode || '',
          quantity: item.totalQuantity || item.TotalQuantity || 0,
          unit: item.unit || item.Unit || '',
          salePrice: item.salePrice || item.SalePrice || 0,
          imageUrl: item.imageUrl || item.ImageUrl || null,
          categoryId: item.categoryId || item.CategoryId || null,
        }));

        setItems(parsedItems);
        setTotalCount(total);

        console.log(`✅ [SINGLE PRODUCT MODAL] ${parsedItems.length} ürün yüklendi (Toplam: ${total})`);
      } catch (error: any) {
        console.error('❌ [SINGLE PRODUCT MODAL] Ürün yükleme hatası:', error);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [open, companyId, page, rowsPerPage, searchTerm, selectedWarehouseId, selectedCategoryId]);

  // Pagination handlers
  const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Ürün seçimi
  const handleSelectProduct = (productId: string) => {
    setSelectedProductId(productId);
  };

  // Seçimi onayla
  const handleConfirmSelection = () => {
    const selectedProduct = items.find(item => item.id === selectedProductId);
    if (selectedProduct) {
      onSelectProduct(selectedProduct);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryIcon color="primary" />
            <Typography variant="h6">{tt('Ürün Seçimi', 'Product Selection')}</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filtreler */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Ürün adı, barkod, stok kodu...', 'Product name, barcode, stock code...')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="warehouse-filter-label">{tt('Depo', 'Warehouse')}</InputLabel>
              <Select
                labelId="warehouse-filter-label"
                value={selectedWarehouseId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedWarehouseId(value);
                  setPage(0);
                }}
                label={tt('Depo', 'Warehouse')}
                startAdornment={
                  <InputAdornment position="start">
                    <WarehouseIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{tt('Tüm Depolar', 'All Warehouses')}</MenuItem>
                {warehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">{tt('Kategori', 'Category')}</InputLabel>
              <Select
                labelId="category-filter-label"
                value={selectedCategoryId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedCategoryId(value);
                  setPage(0);
                }}
                label={tt('Kategori', 'Category')}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{tt('Tüm Kategoriler', 'All Categories')}</MenuItem>
                {categories.sort((a, b) => a.name.localeCompare(b.name)).map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Ürün Tablosu */}
        <Box sx={{ position: 'relative', minHeight: 400 }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
            }}>
              <CircularProgress />
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: 'grey.100' }}>{tt('Seç', 'Select')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>{tt('Ürün Adı', 'Product Name')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>{tt('Stok Kodu', 'Stock Code')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>{tt('Barkod', 'Barcode')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="right">{tt('Miktar', 'Quantity')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="right">{tt('Satış Fiyatı', 'Sale Price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {tt('Filtre kriterlerine uygun ürün bulunamadı.', 'No product found for selected filter criteria.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const isSelected = selectedProductId === item.id;

                    return (
                      <TableRow
                        hover
                        onClick={() => handleSelectProduct(item.id)}
                        selected={isSelected}
                        key={item.id}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Radio
                            checked={isSelected}
                            onChange={() => handleSelectProduct(item.id)}
                            value={item.id}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.stockCode || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.barcode || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.quantity || 0} {item.unit || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color="primary.main">
                            {item.salePrice?.toLocaleString(localeCode, { minimumFractionDigits: 2 })} ₺
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) =>
            isTr ? `${count} üründen ${from}-${to} arası` : `${from}-${to} of ${count} products`
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={handleConfirmSelection} 
          variant="contained"
          disabled={!selectedProductId}
        >
          {tt('Ürünü Seç', 'Select Product')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SingleProductSelectionModal;
